import { Loader2, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { ACCEPTED_IMAGE_TYPES } from '@/shared/constants'
import { uploadImageWithValidation } from '@/shared/lib/upload/upload-image-with-validation'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { Button } from '@/shared/ui/button'

interface ImageUploadFieldTexts {
  select: string
  uploading: string
  empty: string
  previewAlt: string
}

interface ImageUploadFieldProps {
  label: string
  required?: boolean
  value: string
  onChange: (url: string) => void
  texts: ImageUploadFieldTexts
  disabled?: boolean
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon'
  buttonClassName?: string
  acceptTypes?: readonly string[]
  maxSizeMB?: number
  showValueText?: boolean
  previewWrapperClassName?: string
  previewButtonClassName?: string
  previewImageClassName?: string
  emptyTextClassName?: string
  onPreview?: (url: string) => void
  onUploadingChange?: (isUploading: boolean) => void
  onUploadSuccess?: () => void
  onUploadError?: (message: string) => void
  uploadFailedText?: string
}

function revokeObjectUrl(url: string | null) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

export function ImageUploadField({
  label,
  required,
  value,
  onChange,
  texts,
  disabled = false,
  buttonSize = 'default',
  buttonClassName = 'w-full justify-start',
  acceptTypes = ACCEPTED_IMAGE_TYPES,
  maxSizeMB,
  showValueText = false,
  previewWrapperClassName = 'mt-3 min-h-[180px] border border-zinc-200 rounded-xl overflow-hidden bg-zinc-50 grid place-items-center',
  previewButtonClassName = 'w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
  previewImageClassName = 'w-full max-h-60 object-cover block cursor-zoom-in',
  emptyTextClassName = 'text-sm text-zinc-400',
  onPreview,
  onUploadingChange,
  onUploadSuccess,
  onUploadError,
  uploadFailedText = 'Upload failed',
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    onUploadingChange?.(isUploading)
  }, [isUploading, onUploadingChange])

  useEffect(() => {
    setPreviewUrl((prev) => {
      revokeObjectUrl(prev)
      return null
    })
  }, [value])

  useEffect(() => {
    return () => {
      revokeObjectUrl(previewUrl)
    }
  }, [previewUrl])

  async function handleFileChange(file: File | null) {
    if (!file) {
      return
    }

    const nextPreview = URL.createObjectURL(file)
    setPreviewUrl((prev) => {
      revokeObjectUrl(prev)
      return nextPreview
    })
    setIsUploading(true)

    try {
      const uploaded = await uploadImageWithValidation({
        file,
        acceptedTypes: acceptTypes,
        ...(maxSizeMB !== undefined ? { maxSizeMB } : {}),
      })
      onChange(uploaded.url)
      onUploadSuccess?.()
    } catch (error) {
      const message =
        getApiErrorText(error) ?? (error instanceof Error ? error.message : uploadFailedText)
      onUploadError?.(message)
    } finally {
      setIsUploading(false)
    }
  }

  const displaySource = (previewUrl ?? value).trim()

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-zinc-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      <input
        ref={inputRef}
        type="file"
        accept={acceptTypes?.join(',')}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null
          event.target.value = ''
          void handleFileChange(file)
        }}
      />
      <Button
        type="button"
        variant="outline"
        size={buttonSize}
        className={buttonClassName}
        disabled={disabled || isUploading}
        onClick={() => inputRef.current?.click()}
      >
        {isUploading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {texts.uploading}
          </>
        ) : (
          <>
            <Upload className="mr-2 h-4 w-4" />
            {texts.select}
          </>
        )}
      </Button>
      {showValueText && value && <p className="text-xs text-zinc-500 break-all">{value}</p>}

      <div className={previewWrapperClassName}>
        {displaySource ? (
          <button
            type="button"
            className={previewButtonClassName}
            onClick={() => onPreview?.(displaySource)}
          >
            <img
              src={displaySource}
              alt={texts.previewAlt}
              className={previewImageClassName}
              onError={(event) => {
                ;(event.target as HTMLImageElement).style.display = 'none'
              }}
            />
          </button>
        ) : (
          <p className={emptyTextClassName}>{texts.empty}</p>
        )}
      </div>
    </div>
  )
}
